import { Routes, Route, Navigate } from 'react-router-dom';
import { IdentityProvider, useIdentity } from './lib/identity';
import Layout from './components/Layout';

import HomePage from './modules/home/HomePage';
import BudgetPage from './modules/money/BudgetPage';
import TransactionsPage from './modules/money/TransactionsPage';
import InsightsPage from './modules/insights/InsightsPage';
import MealsPage from './modules/meals/MealsPage';
import ChoresPage from './modules/chores/ChoresPage';
import CalendarPage from './modules/calendar/CalendarPage';
import LocationPage from './modules/location/LocationPage';
import VaultPage from './modules/vault/VaultPage';
import InsurancePage from './modules/insurance/InsurancePage';
import WardrobePage from './modules/wardrobe/WardrobePage';
import WellnessPage from './modules/wellness/WellnessPage';
import AdminPage from './modules/admin/AdminPage';
import NotificationsPage from './modules/notifications/NotificationsPage';
import MessagesPage from './modules/messages/MessagesPage';
import { ProfilePage } from './modules/profile/ProfilePage';
import { KidMoneyPage } from './modules/kidmoney/KidMoneyPage';
import BirthdaysPage from './modules/birthdays/BirthdaysPage';
import { RemindersPage } from './modules/notifications-extras/RemindersPage';
import AssistantPage from './modules/assistant/AssistantPage';
import KioskPage from './modules/kiosk/KioskPage';
import MorePage from './modules/more/MorePage';

/**
 * Front-end gate for adult-only screens. This is a courtesy so kids don't see
 * dead links — it is NOT the security boundary. The Worker enforces the real one
 * (worker/middleware/auth.ts). Never rely on this alone.
 */
function AdultOnly({ children }: { children: React.ReactNode }) {
  const identity = useIdentity();
  if (identity.role !== 'adult') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />

        <Route path="/money" element={<AdultOnly><BudgetPage /></AdultOnly>} />
        <Route path="/money/transactions" element={<AdultOnly><TransactionsPage /></AdultOnly>} />
        <Route path="/money/insights" element={<AdultOnly><InsightsPage /></AdultOnly>} />
        <Route path="/vault" element={<AdultOnly><VaultPage /></AdultOnly>} />
        <Route path="/money/insurance" element={<AdultOnly><InsurancePage /></AdultOnly>} />
        {/* Insurance lives under Money, alongside /money/insights. The bare /insurance
            path shipped first and may be bookmarked or typed, so it redirects rather
            than falling through to the catch-all and silently landing on Home. */}
        <Route path="/insurance" element={<Navigate to="/money/insurance" replace />} />
        <Route path="/admin" element={<AdultOnly><AdminPage /></AdultOnly>} />

        <Route path="/meals" element={<MealsPage />} />
        <Route path="/chores" element={<ChoresPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/location" element={<LocationPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        {/* The board left the nav in the 5-tab rebuild (Aug 31, 2026) but is NOT dead:
            its entry point is the MessageBoardCard on the home hub ("Open board"), which
            also composes. Deliberately absent from PRIMARY_NAV and MORE_NAV — do not
            re-add it there. The profile is the one screen that is entirely yours (the
            Worker derives who you are from the verified identity — there is no id in
            any of those calls to argue with). */}
        <Route path="/board" element={<MessagesPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Kids' own money view (adults see all kids); the wallet-level Worker gate
            means a kid only ever gets their own data regardless of the URL. */}
        <Route path="/allowance" element={<KidMoneyPage />} />
        <Route path="/birthdays" element={<BirthdaysPage />} />
        <Route path="/reminders" element={<RemindersPage />} />
        {/* Household-wide: everyone gets their own wardrobe, and the Worker scopes it. */}
        <Route path="/wardrobe" element={<WardrobePage />} />
        {/* Wellness is adult-only AND member-scoped inside the Worker — an adult sees only
            their own readings, which is stricter than every other module here. */}
        <Route path="/wellness" element={<AdultOnly><WellnessPage /></AdultOnly>} />
        <Route path="/ask" element={<AssistantPage />} />
        {/* Index for everything that isn't one of the five bottom tabs. */}
        <Route path="/more" element={<MorePage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <IdentityProvider>
      <Routes>
        {/* The kiosk is a full-screen kitchen display — no nav, no chrome. It still
            sits behind Cloudflare Access (the iPad has a long session) and reads only
            household-wide, non-money endpoints. Kept OUTSIDE Layout on purpose. */}
        <Route path="/kiosk" element={<KioskPage />} />
        <Route path="*" element={<AppRoutes />} />
      </Routes>
    </IdentityProvider>
  );
}
